// SNIGJ - Enhanced Prisoner Detail Page
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { prisonersAPI, referenceAPI, documentsAPI } from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import BiometricCaptureModal from '../components/BiometricCaptureModal';
import AccessDeniedAlert from '../components/AccessDeniedAlert';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  ArrowLeft, Save, User, Upload, Camera, Fingerprint, FileText, Calendar, 
  MapPin, Shield, Eye, Download, Image, File, Phone, Mail, Home, Heart,
  AlertTriangle, Clock, Scale, Briefcase, Users, Activity, Printer,
  Share2, Lock, Unlock, History, Globe, CreditCard, ScanLine
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Crime types with translations
const CRIME_TYPES = [
  { value: 'VOL', label: { fr: 'Vol', en: 'Theft' } },
  { value: 'VIOL', label: { fr: 'Viol', en: 'Rape' } },
  { value: 'MEURTRE', label: { fr: 'Meurtre/Assassinat', en: 'Murder' } },
  { value: 'DETOURNEMENT', label: { fr: 'Détournement de fonds', en: 'Embezzlement' } },
  { value: 'ESCROQUERIE', label: { fr: 'Escroquerie', en: 'Fraud' } },
  { value: 'TRAFIC_DROGUE', label: { fr: 'Trafic de drogue', en: 'Drug Trafficking' } },
  { value: 'TRAFIC_HUMAINS', label: { fr: 'Trafic d\'êtres humains', en: 'Human Trafficking' } },
  { value: 'VIOLENCE', label: { fr: 'Violence/Coups et blessures', en: 'Assault' } },
  { value: 'CORRUPTION', label: { fr: 'Corruption', en: 'Corruption' } },
  { value: 'TERRORISME', label: { fr: 'Terrorisme', en: 'Terrorism' } },
  { value: 'REBELLION', label: { fr: 'Rébellion', en: 'Rebellion' } },
  { value: 'AUTRE', label: { fr: 'Autre', en: 'Other' } }
];

const PrisonerDetailPage = () => {
  const { prisonerId } = useParams();
  const navigate = useNavigate();
  const { language, user } = useAuth();
  const isNew = prisonerId === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [prisons, setPrisons] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [relatedCase, setRelatedCase] = useState(null);
  const [visitHistory, setVisitHistory] = useState([]);
  const [medicalHistory, setMedicalHistory] = useState([]);
  const [showFingerprintModal, setShowFingerprintModal] = useState(false);
  const [showRetinalModal, setShowRetinalModal] = useState(false);
  const [accessDenied, setAccessDenied] = useState(null);
  
  const [formData, setFormData] = useState({
    unji: '',
    first_name: '',
    last_name: '',
    date_of_birth: '',
    place_of_birth: '',
    gender: 'M',
    nationality: 'Congolaise',
    national_id: '',
    passport_number: '',
    status: 'PREVENTIVE',
    classification: 'RESTRICTED',
    is_classified: false,
    prison_id: '',
    cell_number: '',
    block: '',
    admission_date: new Date().toISOString().split('T')[0],
    sentence_start: '',
    sentence_end: '',
    release_date: '',
    charges: [],
    crime_types: [],
    case_id: '',
    biometric_photo: '',
    fingerprint_hash: '',
    // Contact information
    address: '',
    phone: '',
    email: '',
    // Emergency contacts
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relation: '',
    // Physical description
    height: '',
    weight: '',
    eye_color: '',
    hair_color: '',
    distinguishing_marks: '',
    blood_type: '',
    // Medical
    medical_conditions: '',
    allergies: '',
    current_medications: '',
    // Legal
    lawyer_name: '',
    lawyer_phone: '',
    lawyer_email: '',
    court_name: '',
    judge_name: '',
    verdict: '',
    appeal_status: '',
    // Additional
    education_level: '',
    profession: '',
    marital_status: '',
    children_count: 0,
    religious_affiliation: '',
    languages_spoken: '',
    notes: '',
    risk_level: 'MEDIUM',
    behavior_notes: ''
  });

  const loadPrisons = useCallback(async () => {
    try {
      const response = await referenceAPI.getPrisons();
      setPrisons(response.data);
    } catch (error) {
      console.error('Failed to load prisons');
    }
  }, []);

  const loadPrisoner = useCallback(async () => {
    try {
      setLoading(true);
      const response = await prisonersAPI.get(prisonerId);
      setFormData(prev => ({ ...prev, ...response.data }));
      
      // Load attachments
      if (response.data.prisoner_id) {
        const docsResponse = await documentsAPI.list({ prisoner_id: response.data.prisoner_id });
        setAttachments(docsResponse.data || []);
      }
      
      // Load related case if exists
      if (response.data.case_id) {
        try {
          const caseResponse = await api.get(`/cases/${response.data.case_id}`);
          setRelatedCase(caseResponse.data);
        } catch (e) {
          console.log('No related case found');
        }
      }
    } catch (error) {
      // Check if it's an access denied error
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail || '';
        const needsRequisition = detail.includes('réquisition') || detail.includes('Requisition');
        setAccessDenied({
          reason: detail,
          needsRequisition
        });
      } else {
        toast.error(language === 'fr' ? 'Erreur de chargement' : 'Failed to load prisoner');
        navigate('/prisoners');
      }
    } finally {
      setLoading(false);
    }
  }, [prisonerId, language, navigate]);

  useEffect(() => {
    loadPrisons();
    if (!isNew) {
      loadPrisoner();
    }
  }, [isNew, loadPrisons, loadPrisoner]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        await prisonersAPI.create(formData);
        toast.success(language === 'fr' ? 'Détenu enregistré' : 'Prisoner registered');
      } else {
        await api.put(`/prisoners/${prisonerId}`, formData);
        toast.success(language === 'fr' ? 'Détenu mis à jour' : 'Prisoner updated');
      }
      navigate('/prisoners');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de sauvegarde' : 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(language === 'fr' ? 'Photo trop volumineuse (max 5MB)' : 'Photo too large (max 5MB)');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, biometric_photo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('title', file.name);
        formDataUpload.append('document_type', file.type.startsWith('image/') ? 'IMAGE' : 'OTHER');
        formDataUpload.append('classification', formData.classification);
        if (!isNew && prisonerId) {
          formDataUpload.append('prisoner_id', prisonerId);
        }
        
        await documentsAPI.upload(formDataUpload);
        toast.success(`${file.name} ${language === 'fr' ? 'téléversé' : 'uploaded'}`);
        
        if (!isNew) {
          const docsResponse = await documentsAPI.list({ prisoner_id: prisonerId });
          setAttachments(docsResponse.data || []);
        }
      } catch (error) {
        toast.error(`${language === 'fr' ? 'Erreur:' : 'Error:'} ${file.name}`);
      }
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const calculateTimeServed = () => {
    const start = formData.sentence_start || formData.admission_date;
    if (!start) return null;
    const startDate = new Date(start);
    const today = new Date();
    const diffTime = Math.abs(today - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    const days = diffDays % 30;
    
    if (years > 0) return `${years} ${language === 'fr' ? 'an(s)' : 'year(s)'}, ${months} ${language === 'fr' ? 'mois' : 'months'}`;
    if (months > 0) return `${months} ${language === 'fr' ? 'mois' : 'months'}, ${days} ${language === 'fr' ? 'jours' : 'days'}`;
    return `${days} ${language === 'fr' ? 'jours' : 'days'}`;
  };

  const JUDICIAL_ROLES = [
    'SUPER_ADMIN', 'JUGE_PAIX', 'PRESIDENT_TRIBUNAL_PAIX',
    'CONSEILLER_APPEL', 'PRESIDENT_COUR_APPEL',
    'JUGE_SUPREME', 'PRESIDENT_COUR_SUPREME',
    'CONSEILLER_CASSATION', 'PRESIDENT_CASSATION',
    'SUBSTITUT_PROCUREUR', 'PROCUREUR_REPUBLIQUE',
    'AVOCAT_GENERAL', 'PROCUREUR_GENERAL'
  ];
  const TRANSFER_RELEASE_ROLES = [...JUDICIAL_ROLES, 'NATIONAL_ADMIN', 'DIRECTEUR_PRISON', 'MINISTRE_JUSTICE', 'PRESIDENT'];
  
  const getAvailableStatuses = () => {
    const role = user?.role || '';
    const base = ['PREVENTIVE'];
    if (JUDICIAL_ROLES.includes(role)) base.push('SENTENCED');
    if (TRANSFER_RELEASE_ROLES.includes(role)) { base.push('RELEASED', 'TRANSFERRED'); }
    if (['SUPER_ADMIN', 'NATIONAL_ADMIN', 'DIRECTEUR_PRISON'].includes(role)) base.push('DECEASED');
    // Always include the current status so it shows correctly
    if (formData.status && !base.includes(formData.status)) base.push(formData.status);
    return [...new Set(base)];
  };
  
  const getSelectedPrisonCells = () => {
    if (!formData.prison_id) return [];
    const selectedPrison = prisons.find(p => p.prison_id === formData.prison_id);
    return (selectedPrison?.cells || []).filter(c => c.is_active !== false);
  };
  
  const classifications = ['PUBLIC', 'RESTRICTED', 'CONFIDENTIAL', 'SECRET', 'TOP_SECRET'];
  const riskLevels = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
  const maritalStatuses = ['SINGLE', 'MARRIED', 'DIVORCED', 'WIDOWED'];
  const educationLevels = ['NONE', 'PRIMARY', 'SECONDARY', 'UNIVERSITY', 'POSTGRADUATE'];

  const getStatusBadge = (status) => {
    const styles = {
      PREVENTIVE: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      SENTENCED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      RELEASED: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      TRANSFERRED: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      DECEASED: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    };
    return styles[status] || 'bg-gray-100';
  };

  const getRiskBadge = (risk) => {
    const styles = {
      LOW: 'bg-green-100 text-green-800',
      MEDIUM: 'bg-yellow-100 text-yellow-800',
      HIGH: 'bg-orange-100 text-orange-800',
      CRITICAL: 'bg-red-100 text-red-800'
    };
    return styles[risk] || 'bg-gray-100';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <div className="h-8 w-64 skeleton" />
          <div className="h-96 skeleton" />
        </div>
      </DashboardLayout>
    );
  }

  // Show access denied screen if applicable
  if (accessDenied) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-8">
          <Button variant="ghost" className="mb-4" onClick={() => navigate('/prisoners')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Retour aux détenus' : 'Back to prisoners'}
          </Button>
          <AccessDeniedAlert
            reason={accessDenied.reason}
            needsRequisition={accessDenied.needsRequisition}
            resourceType="PRISONER"
            resourceId={prisonerId}
            resourceTitle={`Détenu ${prisonerId}`}
          />
        </div>
      </DashboardLayout>
    );
  }

  const age = calculateAge(formData.date_of_birth);
  const timeServed = calculateTimeServed();

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="prisoner-detail-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/prisoners')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold">
                {isNew 
                  ? (language === 'fr' ? 'Nouveau Détenu' : 'New Prisoner')
                  : `${formData.first_name} ${formData.last_name}`
                }
              </h1>
              {!isNew && (
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="font-mono text-sm text-muted-foreground">{formData.unji}</span>
                  <Badge className={getStatusBadge(formData.status)}>
                    {t(language, `prisoners.${formData.status?.toLowerCase()}`)}
                  </Badge>
                  <Badge className={getRiskBadge(formData.risk_level)}>
                    {language === 'fr' ? 'Risque' : 'Risk'}: {formData.risk_level}
                  </Badge>
                  {formData.is_classified && (
                    <Badge variant="destructive" className="gap-1">
                      <Lock className="w-3 h-3" /> {language === 'fr' ? 'Classifié' : 'Classified'}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Imprimer' : 'Print'}
            </Button>
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              <Save className="w-4 h-4" />
              {saving ? (language === 'fr' ? 'Sauvegarde...' : 'Saving...') : t(language, 'common.save')}
            </Button>
          </div>
        </div>

        {/* Quick Stats for existing prisoner */}
        {!isNew && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border-blue-200">
              <CardContent className="p-4 text-center">
                <Calendar className="w-6 h-6 mx-auto text-blue-600 mb-1" />
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Âge' : 'Age'}</p>
                <p className="text-xl font-bold">{age || '—'} {language === 'fr' ? 'ans' : 'y/o'}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-950 dark:to-orange-900 border-orange-200">
              <CardContent className="p-4 text-center">
                <Clock className="w-6 h-6 mx-auto text-orange-600 mb-1" />
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Temps détenu' : 'Time Served'}</p>
                <p className="text-lg font-bold">{timeServed || '—'}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 border-purple-200">
              <CardContent className="p-4 text-center">
                <MapPin className="w-6 h-6 mx-auto text-purple-600 mb-1" />
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Cellule' : 'Cell'}</p>
                <p className="text-xl font-bold">{formData.cell_number || '—'}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900 border-green-200">
              <CardContent className="p-4 text-center">
                <FileText className="w-6 h-6 mx-auto text-green-600 mb-1" />
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Documents' : 'Documents'}</p>
                <p className="text-xl font-bold">{attachments.length}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950 dark:to-red-900 border-red-200">
              <CardContent className="p-4 text-center">
                <AlertTriangle className="w-6 h-6 mx-auto text-red-600 mb-1" />
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Chefs d\'accusation' : 'Charges'}</p>
                <p className="text-xl font-bold">{formData.crime_types?.length || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 dark:from-cyan-950 dark:to-cyan-900 border-cyan-200">
              <CardContent className="p-4 text-center">
                <Scale className="w-6 h-6 mx-auto text-cyan-600 mb-1" />
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Dossier lié' : 'Case'}</p>
                <p className="text-xl font-bold">{relatedCase ? '1' : '0'}</p>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Column - Photo & Quick Info */}
          <div className="space-y-6">
            {/* Photo Card */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Camera className="w-5 h-5" />
                  {language === 'fr' ? 'Photo d\'identité' : 'ID Photo'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  {formData.biometric_photo ? (
                    <div className="relative inline-block">
                      <img 
                        src={formData.biometric_photo} 
                        alt="Photo biométrique" 
                        className="w-full max-w-[200px] aspect-[3/4] object-cover rounded-lg border-4 border-muted mx-auto shadow-lg"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-8 w-8 rounded-full"
                        onClick={() => setFormData({ ...formData, biometric_photo: '' })}
                      >
                        ×
                      </Button>
                    </div>
                  ) : (
                    <label className="block w-full max-w-[200px] aspect-[3/4] mx-auto border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors flex flex-col items-center justify-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                      <Camera className="w-16 h-16 text-muted-foreground mb-2" />
                      <span className="text-sm text-muted-foreground text-center px-4">
                        {language === 'fr' ? 'Cliquez pour ajouter une photo' : 'Click to add photo'}
                      </span>
                    </label>
                  )}
                </div>

                {/* Fingerprint Status */}
                <div className="p-4 rounded-lg border bg-muted/30 text-center">
                  <Fingerprint className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm font-medium">
                    {language === 'fr' ? 'Empreintes digitales' : 'Fingerprints'}
                  </p>
                  <Badge variant={formData.fingerprint_hash ? 'default' : 'outline'} className="mt-2">
                    {formData.fingerprint_hash 
                      ? (language === 'fr' ? 'Enregistrées' : 'Recorded') 
                      : (language === 'fr' ? 'Non enregistrées' : 'Not recorded')}
                  </Badge>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3 w-full"
                    onClick={() => setShowFingerprintModal(true)}
                  >
                    <Fingerprint className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Capturer' : 'Capture'}
                  </Button>
                </div>

                {/* Retinal Scan Status */}
                <div className="p-4 rounded-lg border bg-muted/30 text-center">
                  <ScanLine className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm font-medium">
                    {language === 'fr' ? 'Scan rétinien' : 'Retinal Scan'}
                  </p>
                  <Badge variant={formData.retinal_scan ? 'default' : 'outline'} className="mt-2">
                    {formData.retinal_scan 
                      ? (language === 'fr' ? 'Enregistré' : 'Recorded') 
                      : (language === 'fr' ? 'Non enregistré' : 'Not recorded')}
                  </Badge>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3 w-full"
                    onClick={() => setShowRetinalModal(true)}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Capturer' : 'Capture'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Physical Description */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {language === 'fr' ? 'Description physique' : 'Physical Description'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{language === 'fr' ? 'Taille (cm)' : 'Height (cm)'}</Label>
                    <Input
                      type="number"
                      value={formData.height || ''}
                      onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                      placeholder="175"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{language === 'fr' ? 'Poids (kg)' : 'Weight (kg)'}</Label>
                    <Input
                      type="number"
                      value={formData.weight || ''}
                      onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                      placeholder="70"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{language === 'fr' ? 'Yeux' : 'Eyes'}</Label>
                    <Input
                      value={formData.eye_color || ''}
                      onChange={(e) => setFormData({ ...formData, eye_color: e.target.value })}
                      placeholder={language === 'fr' ? 'Marron' : 'Brown'}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{language === 'fr' ? 'Cheveux' : 'Hair'}</Label>
                    <Input
                      value={formData.hair_color || ''}
                      onChange={(e) => setFormData({ ...formData, hair_color: e.target.value })}
                      placeholder={language === 'fr' ? 'Noir' : 'Black'}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">{language === 'fr' ? 'Groupe sanguin' : 'Blood Type'}</Label>
                  <Select
                    value={formData.blood_type || ''}
                    onValueChange={(value) => setFormData({ ...formData, blood_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={language === 'fr' ? 'Sélectionner' : 'Select'} />
                    </SelectTrigger>
                    <SelectContent>
                      {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bt => (
                        <SelectItem key={bt} value={bt}>{bt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{language === 'fr' ? 'Signes distinctifs' : 'Distinguishing Marks'}</Label>
                  <Textarea
                    value={formData.distinguishing_marks || ''}
                    onChange={(e) => setFormData({ ...formData, distinguishing_marks: e.target.value })}
                    placeholder={language === 'fr' ? 'Cicatrices, tatouages...' : 'Scars, tattoos...'}
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Main Tabs */}
          <Card className="lg:col-span-3">
            <Tabs defaultValue="identity" className="h-full">
              <CardHeader className="pb-2">
                <TabsList className="grid w-full grid-cols-3 md:grid-cols-6">
                  <TabsTrigger value="identity">{language === 'fr' ? 'Identité' : 'Identity'}</TabsTrigger>
                  <TabsTrigger value="detention">{language === 'fr' ? 'Détention' : 'Detention'}</TabsTrigger>
                  <TabsTrigger value="legal">{language === 'fr' ? 'Juridique' : 'Legal'}</TabsTrigger>
                  <TabsTrigger value="medical">{language === 'fr' ? 'Médical' : 'Medical'}</TabsTrigger>
                  <TabsTrigger value="contacts">{language === 'fr' ? 'Contacts' : 'Contacts'}</TabsTrigger>
                  <TabsTrigger value="documents">{language === 'fr' ? 'Documents' : 'Documents'}</TabsTrigger>
                </TabsList>
              </CardHeader>
              <CardContent className="pt-4">
                {/* Identity Tab */}
                <TabsContent value="identity" className="space-y-6 mt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Basic Info */}
                    <div className="space-y-4">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                        {language === 'fr' ? 'Informations de base' : 'Basic Information'}
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t(language, 'prisoners.unji')}</Label>
                          <Input
                            value={formData.unji}
                            onChange={(e) => setFormData({ ...formData, unji: e.target.value })}
                            placeholder="UNJI-2024-XXXXX"
                            className="font-mono"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t(language, 'cases.classification')}</Label>
                          <Select
                            value={formData.classification}
                            onValueChange={(value) => setFormData({ ...formData, classification: value })}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {classifications.map((c) => (
                                <SelectItem key={c} value={c}>{t(language, `classifications.${c}`)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t(language, 'prisoners.firstName')}</Label>
                          <Input
                            value={formData.first_name}
                            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t(language, 'prisoners.lastName')}</Label>
                          <Input
                            value={formData.last_name}
                            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>{t(language, 'prisoners.dateOfBirth')}</Label>
                          <Input
                            type="date"
                            value={formData.date_of_birth}
                            onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t(language, 'prisoners.gender')}</Label>
                          <Select
                            value={formData.gender}
                            onValueChange={(value) => setFormData({ ...formData, gender: value })}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="M">{t(language, 'prisoners.male')}</SelectItem>
                              <SelectItem value="F">{t(language, 'prisoners.female')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>{t(language, 'prisoners.nationality')}</Label>
                          <Input
                            value={formData.nationality}
                            onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Lieu de naissance' : 'Place of Birth'}</Label>
                        <Input
                          value={formData.place_of_birth || ''}
                          onChange={(e) => setFormData({ ...formData, place_of_birth: e.target.value })}
                          placeholder={language === 'fr' ? 'Ville, Province' : 'City, Province'}
                        />
                      </div>
                    </div>

                    {/* Personal Info */}
                    <div className="space-y-4">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                        {language === 'fr' ? 'Informations personnelles' : 'Personal Information'}
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'N° Carte d\'identité' : 'ID Card Number'}</Label>
                          <Input
                            value={formData.national_id || ''}
                            onChange={(e) => setFormData({ ...formData, national_id: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'N° Passeport' : 'Passport Number'}</Label>
                          <Input
                            value={formData.passport_number || ''}
                            onChange={(e) => setFormData({ ...formData, passport_number: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'État civil' : 'Marital Status'}</Label>
                          <Select
                            value={formData.marital_status || ''}
                            onValueChange={(value) => setFormData({ ...formData, marital_status: value })}
                          >
                            <SelectTrigger><SelectValue placeholder={language === 'fr' ? 'Sélectionner' : 'Select'} /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="SINGLE">{language === 'fr' ? 'Célibataire' : 'Single'}</SelectItem>
                              <SelectItem value="MARRIED">{language === 'fr' ? 'Marié(e)' : 'Married'}</SelectItem>
                              <SelectItem value="DIVORCED">{language === 'fr' ? 'Divorcé(e)' : 'Divorced'}</SelectItem>
                              <SelectItem value="WIDOWED">{language === 'fr' ? 'Veuf/Veuve' : 'Widowed'}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Nombre d\'enfants' : 'Children'}</Label>
                          <Input
                            type="number"
                            min="0"
                            value={formData.children_count || 0}
                            onChange={(e) => setFormData({ ...formData, children_count: parseInt(e.target.value) || 0 })}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Niveau d\'études' : 'Education'}</Label>
                          <Select
                            value={formData.education_level || ''}
                            onValueChange={(value) => setFormData({ ...formData, education_level: value })}
                          >
                            <SelectTrigger><SelectValue placeholder={language === 'fr' ? 'Sélectionner' : 'Select'} /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="NONE">{language === 'fr' ? 'Aucun' : 'None'}</SelectItem>
                              <SelectItem value="PRIMARY">{language === 'fr' ? 'Primaire' : 'Primary'}</SelectItem>
                              <SelectItem value="SECONDARY">{language === 'fr' ? 'Secondaire' : 'Secondary'}</SelectItem>
                              <SelectItem value="UNIVERSITY">{language === 'fr' ? 'Universitaire' : 'University'}</SelectItem>
                              <SelectItem value="POSTGRADUATE">{language === 'fr' ? 'Post-universitaire' : 'Postgraduate'}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Profession' : 'Profession'}</Label>
                          <Input
                            value={formData.profession || ''}
                            onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Religion' : 'Religion'}</Label>
                          <Input
                            value={formData.religious_affiliation || ''}
                            onChange={(e) => setFormData({ ...formData, religious_affiliation: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Langues parlées' : 'Languages'}</Label>
                          <Input
                            value={formData.languages_spoken || ''}
                            onChange={(e) => setFormData({ ...formData, languages_spoken: e.target.value })}
                            placeholder="Français, Lingala..."
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Detention Tab */}
                <TabsContent value="detention" className="space-y-6 mt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                        {language === 'fr' ? 'Lieu de détention' : 'Detention Location'}
                      </h4>
                      <div className="space-y-2">
                        <Label>{t(language, 'prisoners.prison')}</Label>
                        <Select
                          value={formData.prison_id}
                          onValueChange={(value) => setFormData({ ...formData, prison_id: value, cell_number: '' })}
                        >
                          <SelectTrigger><SelectValue placeholder={language === 'fr' ? 'Sélectionner' : 'Select'} /></SelectTrigger>
                          <SelectContent>
                            {prisons.map((p) => (
                              <SelectItem key={p.prison_id} value={p.prison_id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Bloc/Pavillon' : 'Block'}</Label>
                          <Input
                            value={formData.block || ''}
                            onChange={(e) => setFormData({ ...formData, block: e.target.value })}
                            placeholder="A, B, C..."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'N° Cellule' : 'Cell Number'}</Label>
                          {getSelectedPrisonCells().length > 0 ? (
                            <Select value={formData.cell_number || ''} onValueChange={(value) => setFormData({ ...formData, cell_number: value })}>
                              <SelectTrigger data-testid="prisoner-cell-edit"><SelectValue placeholder={language === 'fr' ? 'Selectionner cellule...' : 'Select cell...'} /></SelectTrigger>
                              <SelectContent>
                                {getSelectedPrisonCells().map((cell) => (
                                  <SelectItem key={cell.cell_id} value={cell.name}>
                                    {cell.name} {cell.pavilion && `(${cell.pavilion})`} - Cap: {cell.capacity}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              value={formData.cell_number || ''}
                              onChange={(e) => setFormData({ ...formData, cell_number: e.target.value })}
                              placeholder="A-101"
                              data-testid="prisoner-cell-edit"
                            />
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Niveau de risque' : 'Risk Level'}</Label>
                        <Select
                          value={formData.risk_level}
                          onValueChange={(value) => setFormData({ ...formData, risk_level: value })}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="LOW">{language === 'fr' ? 'Faible' : 'Low'}</SelectItem>
                            <SelectItem value="MEDIUM">{language === 'fr' ? 'Moyen' : 'Medium'}</SelectItem>
                            <SelectItem value="HIGH">{language === 'fr' ? 'Élevé' : 'High'}</SelectItem>
                            <SelectItem value="CRITICAL">{language === 'fr' ? 'Critique' : 'Critical'}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                        {language === 'fr' ? 'Dates clés' : 'Key Dates'}
                      </h4>
                      <div className="space-y-2">
                        <Label>{t(language, 'prisoners.status')}</Label>
                        <Select
                          value={formData.status}
                          onValueChange={(value) => setFormData({ ...formData, status: value })}
                        >
                          <SelectTrigger data-testid="prisoner-status-edit"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {getAvailableStatuses().map((s) => (
                              <SelectItem key={s} value={s}>{t(language, `prisoners.${s.toLowerCase()}`)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {!JUDICIAL_ROLES.includes(user?.role) && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {language === 'fr' 
                              ? 'Le changement de statut vers "Condamne" necessite un role de magistrat/juge' 
                              : 'Changing status to "Sentenced" requires a judge/magistrate role'}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Date d\'admission' : 'Admission Date'}</Label>
                        <Input
                          type="date"
                          value={formData.admission_date}
                          onChange={(e) => setFormData({ ...formData, admission_date: e.target.value })}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Début de peine' : 'Sentence Start'}</Label>
                          <Input
                            type="date"
                            value={formData.sentence_start || ''}
                            onChange={(e) => setFormData({ ...formData, sentence_start: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Fin de peine' : 'Sentence End'}</Label>
                          <Input
                            type="date"
                            value={formData.sentence_end || ''}
                            onChange={(e) => setFormData({ ...formData, sentence_end: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Date de libération prévue' : 'Expected Release'}</Label>
                        <Input
                          type="date"
                          value={formData.release_date || ''}
                          onChange={(e) => setFormData({ ...formData, release_date: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                      {language === 'fr' ? 'Notes de comportement' : 'Behavior Notes'}
                    </h4>
                    <Textarea
                      value={formData.behavior_notes || ''}
                      onChange={(e) => setFormData({ ...formData, behavior_notes: e.target.value })}
                      placeholder={language === 'fr' ? 'Notes sur le comportement du détenu...' : 'Notes on prisoner behavior...'}
                      rows={4}
                    />
                  </div>
                </TabsContent>

                {/* Legal Tab */}
                <TabsContent value="legal" className="space-y-6 mt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                        {language === 'fr' ? 'Chefs d\'accusation' : 'Charges'}
                      </h4>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Types de crimes/délits' : 'Crime Types'}</Label>
                        <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg">
                          {CRIME_TYPES.map((crime) => (
                            <label key={crime.value} className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={formData.crime_types?.includes(crime.value) || false}
                                onChange={(e) => {
                                  const newCrimes = e.target.checked
                                    ? [...(formData.crime_types || []), crime.value]
                                    : (formData.crime_types || []).filter(c => c !== crime.value);
                                  setFormData({ ...formData, crime_types: newCrimes });
                                }}
                                className="rounded"
                              />
                              <span className="text-sm">{crime.label[language]}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Détails des charges' : 'Charge Details'}</Label>
                        <Textarea
                          value={formData.charges?.join('\n') || ''}
                          onChange={(e) => setFormData({ ...formData, charges: e.target.value.split('\n').filter(c => c.trim()) })}
                          placeholder={language === 'fr' ? 'Une charge par ligne...' : 'One charge per line...'}
                          rows={4}
                        />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                        {language === 'fr' ? 'Informations juridiques' : 'Legal Information'}
                      </h4>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'N° Dossier lié' : 'Related Case'}</Label>
                        <Input
                          value={formData.case_id || ''}
                          onChange={(e) => setFormData({ ...formData, case_id: e.target.value })}
                          placeholder="CASE-2024-XXXXX"
                          className="font-mono"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Tribunal' : 'Court'}</Label>
                        <Input
                          value={formData.court_name || ''}
                          onChange={(e) => setFormData({ ...formData, court_name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Juge assigné' : 'Assigned Judge'}</Label>
                        <Input
                          value={formData.judge_name || ''}
                          onChange={(e) => setFormData({ ...formData, judge_name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Verdict' : 'Verdict'}</Label>
                        <Textarea
                          value={formData.verdict || ''}
                          onChange={(e) => setFormData({ ...formData, verdict: e.target.value })}
                          rows={2}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Statut de l\'appel' : 'Appeal Status'}</Label>
                        <Input
                          value={formData.appeal_status || ''}
                          onChange={(e) => setFormData({ ...formData, appeal_status: e.target.value })}
                          placeholder={language === 'fr' ? 'En cours, Rejeté...' : 'Pending, Rejected...'}
                        />
                      </div>
                    </div>
                  </div>

                  {relatedCase && (
                    <>
                      <Separator />
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <h4 className="font-medium mb-2">{language === 'fr' ? 'Dossier lié' : 'Linked Case'}</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">{language === 'fr' ? 'Numéro' : 'Number'}</p>
                            <p className="font-mono">{relatedCase.case_number}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">{language === 'fr' ? 'Titre' : 'Title'}</p>
                            <p>{relatedCase.title}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">{language === 'fr' ? 'Statut' : 'Status'}</p>
                            <Badge>{relatedCase.status}</Badge>
                          </div>
                          <div>
                            <Button variant="outline" size="sm" onClick={() => navigate(`/cases/${relatedCase.case_id}`)}>
                              <Eye className="w-4 h-4 mr-1" /> {language === 'fr' ? 'Voir' : 'View'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </TabsContent>

                {/* Medical Tab */}
                <TabsContent value="medical" className="space-y-6 mt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <Heart className="w-4 h-4" />
                        {language === 'fr' ? 'Conditions médicales' : 'Medical Conditions'}
                      </h4>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Maladies/Conditions' : 'Diseases/Conditions'}</Label>
                        <Textarea
                          value={formData.medical_conditions || ''}
                          onChange={(e) => setFormData({ ...formData, medical_conditions: e.target.value })}
                          placeholder={language === 'fr' ? 'Diabète, hypertension...' : 'Diabetes, hypertension...'}
                          rows={3}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Allergies' : 'Allergies'}</Label>
                        <Textarea
                          value={formData.allergies || ''}
                          onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                          placeholder={language === 'fr' ? 'Pénicilline, arachides...' : 'Penicillin, peanuts...'}
                          rows={2}
                        />
                      </div>
                    </div>
                    <div className="space-y-4">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <Activity className="w-4 h-4" />
                        {language === 'fr' ? 'Traitement en cours' : 'Current Treatment'}
                      </h4>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Médicaments actuels' : 'Current Medications'}</Label>
                        <Textarea
                          value={formData.current_medications || ''}
                          onChange={(e) => setFormData({ ...formData, current_medications: e.target.value })}
                          placeholder={language === 'fr' ? 'Liste des médicaments...' : 'List of medications...'}
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Contacts Tab */}
                <TabsContent value="contacts" className="space-y-6 mt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        {language === 'fr' ? 'Contact d\'urgence' : 'Emergency Contact'}
                      </h4>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Nom complet' : 'Full Name'}</Label>
                        <Input
                          value={formData.emergency_contact_name || ''}
                          onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
                        <Input
                          value={formData.emergency_contact_phone || ''}
                          onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                          placeholder="+243..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Relation' : 'Relationship'}</Label>
                        <Input
                          value={formData.emergency_contact_relation || ''}
                          onChange={(e) => setFormData({ ...formData, emergency_contact_relation: e.target.value })}
                          placeholder={language === 'fr' ? 'Épouse, Frère...' : 'Wife, Brother...'}
                        />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <Scale className="w-4 h-4" />
                        {language === 'fr' ? 'Avocat' : 'Lawyer'}
                      </h4>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Nom de l\'avocat' : 'Lawyer Name'}</Label>
                        <Input
                          value={formData.lawyer_name || ''}
                          onChange={(e) => setFormData({ ...formData, lawyer_name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
                        <Input
                          value={formData.lawyer_phone || ''}
                          onChange={(e) => setFormData({ ...formData, lawyer_phone: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={formData.lawyer_email || ''}
                          onChange={(e) => setFormData({ ...formData, lawyer_email: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                      <Home className="w-4 h-4" />
                      {language === 'fr' ? 'Adresse du détenu' : 'Prisoner Address'}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-2 space-y-2">
                        <Label>{language === 'fr' ? 'Adresse' : 'Address'}</Label>
                        <Input
                          value={formData.address || ''}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'fr' ? 'Téléphone personnel' : 'Personal Phone'}</Label>
                        <Input
                          value={formData.phone || ''}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Documents Tab */}
                <TabsContent value="documents" className="space-y-6 mt-0">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">{language === 'fr' ? 'Documents attachés' : 'Attached Documents'}</h4>
                    <label>
                      <input
                        type="file"
                        multiple
                        onChange={handleFileUpload}
                        className="hidden"
                        disabled={isNew}
                      />
                      <Button variant="outline" disabled={isNew} asChild>
                        <span className="cursor-pointer">
                          <Upload className="w-4 h-4 mr-2" />
                          {language === 'fr' ? 'Ajouter' : 'Add'}
                        </span>
                      </Button>
                    </label>
                  </div>

                  {isNew && (
                    <p className="text-sm text-muted-foreground italic">
                      {language === 'fr' 
                        ? 'Enregistrez d\'abord le détenu pour ajouter des documents' 
                        : 'Save the prisoner first to add documents'}
                    </p>
                  )}

                  {attachments.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {attachments.map((doc) => (
                        <div key={doc.document_id} className="p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                          <div className="flex items-center gap-2 mb-2">
                            {doc.document_type === 'IMAGE' ? (
                              <Image className="w-5 h-5 text-blue-500" />
                            ) : (
                              <File className="w-5 h-5 text-gray-500" />
                            )}
                            <span className="text-sm truncate flex-1">{doc.title}</span>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" className="flex-1" onClick={() => window.open(doc.file_url, '_blank')}>
                              <Eye className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="sm" className="flex-1">
                              <Download className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : !isNew && (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>{language === 'fr' ? 'Aucun document' : 'No documents'}</p>
                    </div>
                  )}

                  <Separator />

                  <div className="space-y-4">
                    <h4 className="font-medium">{language === 'fr' ? 'Notes générales' : 'General Notes'}</h4>
                    <Textarea
                      value={formData.notes || ''}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder={language === 'fr' ? 'Notes additionnelles...' : 'Additional notes...'}
                      rows={4}
                    />
                  </div>
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>
        </div>
      </div>

      {/* Biometric Capture Modals */}
      <BiometricCaptureModal
        open={showFingerprintModal}
        onClose={() => setShowFingerprintModal(false)}
        captureType="fingerprint"
        existingData={formData.fingerprint_hash}
        onCapture={(data) => {
          setFormData(prev => ({ 
            ...prev, 
            fingerprint_hash: JSON.stringify(data.fingerprints),
            fingerprints_data: data
          }));
          toast.success(language === 'fr' ? 'Empreintes capturées' : 'Fingerprints captured');
        }}
      />

      <BiometricCaptureModal
        open={showRetinalModal}
        onClose={() => setShowRetinalModal(false)}
        captureType="retinal"
        existingData={formData.retinal_scan}
        onCapture={(data) => {
          setFormData(prev => ({ 
            ...prev, 
            retinal_scan: JSON.stringify(data.retinalScan)
          }));
          toast.success(language === 'fr' ? 'Scan rétinien capturé' : 'Retinal scan captured');
        }}
      />
    </DashboardLayout>
  );
};

export default PrisonerDetailPage;
